import { ReactElement, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { organisationActions } from '../../redux/slices/organisation.slice';
import { IOrganisationAccess, IOrganisationMemberAccess } from '../../types/@organisation';

interface IProps {
  organisationId: string;
  access: IOrganisationAccess;
  currentUserId: string;
  isSaving: boolean;
}

type TConfirmAction = {
  type: 'removeMember' | 'removeAdmin' | 'removeHelper' | 'leaveOrganisation' | null;
  userId?: string;
  label?: string;
};

const uniqueIds = (values: string[]): string[] => {
  return values.filter((value, index, array) => array.indexOf(value) === index);
};

const OrganisationAccessPanel = ({
  organisationId,
  access,
  currentUserId,
  isSaving,
}: IProps): ReactElement => {
  const dispatch = useDispatch();
  const [selectedAdminUserId, setSelectedAdminUserId] = useState<string>('');
  const [selectedHelperUserId, setSelectedHelperUserId] = useState<string>('');
  const [confirmAction, setConfirmAction] = useState<TConfirmAction>({ type: null });

  const memberMap = useMemo(() => {
    return access.members.reduce((acc: Record<string, IOrganisationMemberAccess>, member) => {
      acc[member.userId] = member;
      return acc;
    }, {});
  }, [access.members]);

  const adminMembers = useMemo(() => {
    return access.admins.map((userId) => memberMap[userId]).filter(Boolean);
  }, [access.admins, memberMap]);

  const helperMembers = useMemo(() => {
    return access.helpers.map((userId) => memberMap[userId]).filter(Boolean);
  }, [access.helpers, memberMap]);

  const eligibleAdminCandidates = useMemo(() => {
    return access.members.filter((member) => access.admins.indexOf(member.userId) === -1);
  }, [access.admins, access.members]);

  const eligibleHelperCandidates = useMemo(() => {
    return access.members.filter(
      (member) =>
        access.helpers.indexOf(member.userId) === -1 &&
        access.admins.indexOf(member.userId) === -1
    );
  }, [access.admins, access.helpers, access.members]);

  const myMemberRecord = useMemo(() => {
    return access.members.find((member) => member.userId === currentUserId) || null;
  }, [access.members, currentUserId]);

  const isAdmin = access.myRole === 'admin';

  const handleAccessSave = (admins: string[], helpers: string[]) => {
    dispatch(
      organisationActions.updateOrganisationAccess({
        id: organisationId,
        payload: {
          admins,
          helpers,
        },
      })
    );
  };

  const handleAddAdmin = () => {
    if (!selectedAdminUserId || !isAdmin) return;

    const nextAdmins = uniqueIds(access.admins.concat(selectedAdminUserId));
    const nextHelpers = access.helpers.filter((id) => id !== selectedAdminUserId);

    handleAccessSave(nextAdmins, nextHelpers);
    setSelectedAdminUserId('');
  };

  const handleRemoveAdmin = (userId: string) => {
    const nextAdmins = access.admins.filter((id) => id !== userId);
    handleAccessSave(nextAdmins, access.helpers);
  };

  const handleAddHelper = () => {
    if (!selectedHelperUserId || !isAdmin) return;

    const nextHelpers = uniqueIds(access.helpers.concat(selectedHelperUserId));
    handleAccessSave(access.admins, nextHelpers);
    setSelectedHelperUserId('');
  };

  const handleRemoveHelper = (userId: string) => {
    const nextHelpers = access.helpers.filter((id) => id !== userId);
    handleAccessSave(access.admins, nextHelpers);
  };

  const handleRemoveMember = (userId: string) => {
    dispatch(
      organisationActions.removeOrganisationMember({
        id: organisationId,
        userId,
      })
    );
  };

  const handleLeaveOrganisation = () => {
    dispatch(
      organisationActions.leaveOrganisation({
        id: organisationId,
      })
    );
  };

  const openConfirm = (nextAction: TConfirmAction) => {
    setConfirmAction(nextAction);
  };

  const closeConfirm = () => {
    setConfirmAction({ type: null });
  };

  const runConfirmedAction = () => {
    if (!confirmAction.type) return;

    if (confirmAction.type === 'removeMember' && confirmAction.userId) {
      handleRemoveMember(confirmAction.userId);
    }

    if (confirmAction.type === 'removeAdmin' && confirmAction.userId) {
      handleRemoveAdmin(confirmAction.userId);
    }

    if (confirmAction.type === 'removeHelper' && confirmAction.userId) {
      handleRemoveHelper(confirmAction.userId);
    }

    if (confirmAction.type === 'leaveOrganisation') {
      handleLeaveOrganisation();
    }

    closeConfirm();
  };

  const getConfirmText = () => {
    switch (confirmAction.type) {
      case 'removeMember':
        return `Remove ${confirmAction.label || 'this member'} from the organisation?`;
      case 'removeAdmin':
        return `Remove admin access from ${confirmAction.label || 'this user'}?`;
      case 'removeHelper':
        return `Remove helper access from ${confirmAction.label || 'this user'}?`;
      case 'leaveOrganisation':
        return 'Are you sure you want to leave this organisation?';
      default:
        return '';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#17324d' }}>
          Access management
        </Typography>
        <Chip label={'My role: ' + (access.myRole || 'none')} color={isAdmin ? 'primary' : 'default'} />
      </Box>

      {!isAdmin && (
        <Alert severity="info">
          You can view membership here. Only organisation admins can update roles or remove members.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1.3fr 1fr' },
          gap: 3,
        }}
      >
        <Paper sx={{ p: 3, borderRadius: '16px' }} elevation={0} variant="outlined">
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#17324d', mb: 2 }}>
            Members
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {access.members.map((member) => {
              const isCurrentUser = member.userId === currentUserId;
              const canRemove = isAdmin && member.ownedThingCount === 0 && !isCurrentUser;
              const canLeave = isCurrentUser && member.ownedThingCount === 0;
              const isMemberOnly =
                access.admins.indexOf(member.userId) === -1 && access.helpers.indexOf(member.userId) === -1;

              return (
                <Box
                  key={member.userId}
                  sx={{
                    p: 2,
                    borderRadius: '14px',
                    border: '1px solid #e3e8ef',
                    background: '#fafbfd',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2,
                    alignItems: { xs: 'flex-start', md: 'center' },
                    flexDirection: { xs: 'column', md: 'row' },
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#17324d' }}>
                      {member.name || member.email || member.userId}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#5f7285' }}>{member.email || 'No email'}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        label={member.ownsCurrentThing ? 'Has current org item' : 'No current org item'}
                        color={member.ownsCurrentThing ? 'success' : 'default'}
                      />
                      <Chip size="small" label={'Items: ' + member.ownedThingCount} />
                      {access.admins.indexOf(member.userId) !== -1 && (
                        <Chip size="small" color="primary" label="Admin" />
                      )}
                      {access.helpers.indexOf(member.userId) !== -1 && (
                        <Chip size="small" color="secondary" label="Helper" />
                      )}
                      {isMemberOnly && <Chip size="small" label="Member" />}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {canRemove && (
                      <Button
                        variant="outlined"
                        color="error"
                        disabled={isSaving}
                        onClick={() =>
                          openConfirm({
                            type: 'removeMember',
                            userId: member.userId,
                            label: member.name || member.email || member.userId,
                          })
                        }
                        sx={{ textTransform: 'none', borderRadius: '12px' }}
                      >
                        Remove member
                      </Button>
                    )}

                    {canLeave && (
                      <Button
                        variant="contained"
                        disabled={isSaving}
                        onClick={() => openConfirm({ type: 'leaveOrganisation' })}
                        sx={{ textTransform: 'none', borderRadius: '12px' }}
                      >
                        Leave organisation
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Paper sx={{ p: 3, borderRadius: '16px' }} elevation={0} variant="outlined">
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#17324d', mb: 2 }}>
              Admins
            </Typography>

            {isAdmin && (
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={selectedAdminUserId}
                    onChange={(event) => setSelectedAdminUserId(event.target.value as string)}
                  >
                    <MenuItem value="">
                      <em>Select member</em>
                    </MenuItem>
                    {eligibleAdminCandidates.map((member) => (
                      <MenuItem key={member.userId} value={member.userId}>
                        {member.name || member.email || member.userId}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  disabled={isSaving || !selectedAdminUserId}
                  onClick={handleAddAdmin}
                  sx={{ textTransform: 'none', borderRadius: '12px', whiteSpace: 'nowrap' }}
                >
                  Add admin
                </Button>
              </Box>
            )}

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {adminMembers.length ? (
                adminMembers.map((member) => (
                  <Box
                    key={member.userId}
                    sx={{
                      p: 1.5,
                      borderRadius: '12px',
                      background: '#fafbfd',
                      border: '1px solid #e3e8ef',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: '#17324d' }}>
                        {member.name || member.email || member.userId}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: '#5f7285' }}>{member.email || '-'}</Typography>
                    </Box>

                    {isAdmin && (
                      <Button
                        variant="text"
                        color="error"
                        disabled={isSaving}
                        onClick={() =>
                          openConfirm({
                            type: 'removeAdmin',
                            userId: member.userId,
                            label: member.name || member.email || member.userId,
                          })
                        }
                        sx={{ textTransform: 'none' }}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                ))
              ) : (
                <Alert severity="warning">No admins found.</Alert>
              )}
            </Box>
          </Paper>

          <Paper sx={{ p: 3, borderRadius: '16px' }} elevation={0} variant="outlined">
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#17324d', mb: 2 }}>
              Helpers
            </Typography>

            {isAdmin && (
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={selectedHelperUserId}
                    onChange={(event) => setSelectedHelperUserId(event.target.value as string)}
                  >
                    <MenuItem value="">
                      <em>Select member</em>
                    </MenuItem>
                    {eligibleHelperCandidates.map((member) => (
                      <MenuItem key={member.userId} value={member.userId}>
                        {member.name || member.email || member.userId}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  disabled={isSaving || !selectedHelperUserId}
                  onClick={handleAddHelper}
                  sx={{ textTransform: 'none', borderRadius: '12px', whiteSpace: 'nowrap' }}
                >
                  Add helper
                </Button>
              </Box>
            )}

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {helperMembers.length ? (
                helperMembers.map((member) => (
                  <Box
                    key={member.userId}
                    sx={{
                      p: 1.5,
                      borderRadius: '12px',
                      background: '#fafbfd',
                      border: '1px solid #e3e8ef',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: '#17324d' }}>
                        {member.name || member.email || member.userId}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: '#5f7285' }}>{member.email || '-'}</Typography>
                    </Box>

                    {isAdmin && (
                      <Button
                        variant="text"
                        color="error"
                        disabled={isSaving}
                        onClick={() =>
                          openConfirm({
                            type: 'removeHelper',
                            userId: member.userId,
                            label: member.name || member.email || member.userId,
                          })
                        }
                        sx={{ textTransform: 'none' }}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                ))
              ) : (
                <Alert severity="info">No helpers assigned.</Alert>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {myMemberRecord && myMemberRecord.ownedThingCount > 0 && (
        <Alert severity="info">
          You currently own organisation items, so you cannot leave this organisation yet.
        </Alert>
      )}

      <Dialog open={!!confirmAction.type} onClose={closeConfirm}>
        <DialogTitle>Confirm action</DialogTitle>
        <DialogContent>
          <DialogContentText>{getConfirmText()}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={runConfirmedAction}
            color="error"
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganisationAccessPanel;